import { z } from "zod";
import { optional, storableText } from "./appointments";
import { recordId } from "./contracts";
import { contractReadQuery, contractStatus, scheduleSync, workDayHours, workDayRate, workDayTasks } from "./daily-work";
import { blankAsNull } from "./fields";

export const SCHEDULE_STAFFING_MAX = 50;

export const SCHEDULE_SLOTS_MAX = 50;

export const SCHEDULE_MONTHLY_RULES_MAX = 20;

export const SCHEDULE_DURATION_LABEL_MAX_LENGTH = 100;

export const SCHEDULE_INSTRUCTIONS_MAX_LENGTH = 2000;

const named = z.object({ id: recordId, name: z.string() });

export const scheduleWeekday = z.number().int().min(0).max(6);

export const monthlyOccurrence = z.enum(["first", "second", "third", "fourth", "last"]);
export type MonthlyOccurrence = z.infer<typeof monthlyOccurrence>;

export const monthlyTaskRule = z.object({ occurrence: monthlyOccurrence, tasks: z.array(z.string()) });
export type MonthlyTaskRule = z.infer<typeof monthlyTaskRule>;

export const scheduleStaffing = z.object({
  id: recordId,
  subcontractor: named.nullable(),
  worker: named.nullable(),
  quantity: z.number().int().positive(),
  rateOverride: z.number().nullable(),
});
export type ScheduleStaffing = z.infer<typeof scheduleStaffing>;

export const scheduleRow = z.object({
  id: recordId,
  contract: z.object({ id: recordId, status: contractStatus, start: z.string(), end: z.string().nullable(), property: named }),
  weekday: scheduleWeekday,
  service: named,
  weeklyTasks: z.array(z.string()),
  monthlyRules: z.array(monthlyTaskRule),
  maxHours: z.number().nullable(),
  durationLabel: z.string().nullable(),
  instructions: z.string().nullable(),
  active: z.boolean(),
  position: z.number().int(),
  staffing: z.array(scheduleStaffing),
  createdAt: z.string(),
});
export type ScheduleRow = z.infer<typeof scheduleRow>;

export const scheduleRowListQuery = contractReadQuery.extend({
  contract: optional(recordId.toLowerCase()),
});
export type ScheduleRowListQuery = z.infer<typeof scheduleRowListQuery>;

export const scheduleRowList = z.object({ items: z.array(scheduleRow) });
export type ScheduleRowList = z.infer<typeof scheduleRowList>;

const rateOverride = workDayRate.nullable().optional();

const workerStaffing = { worker: recordId.toLowerCase(), rateOverride };

const subcontractorStaffing = { subcontractor: recordId.toLowerCase(), quantity: z.number().int().min(1).max(SCHEDULE_SLOTS_MAX).optional(), rateOverride };

export const newStaffingRequest = z.union([z.object(workerStaffing).strict(), z.object(subcontractorStaffing).strict()]);
export type NewStaffingRequest = z.infer<typeof newStaffingRequest>;

const keptId = { id: recordId.toLowerCase().optional() };

export const staffingRequest = z.union([z.object({ ...keptId, ...workerStaffing }).strict(), z.object({ ...keptId, ...subcontractorStaffing }).strict()]);
export type StaffingRequest = z.infer<typeof staffingRequest>;

export const monthlyTaskRuleRequest = z.object({ occurrence: monthlyOccurrence, tasks: workDayTasks }).strict();

const optionalText = (maxLength: number) => z.preprocess(blankAsNull, storableText(maxLength).nullable()).optional();

const rowContent = {
  service: recordId.toLowerCase(),
  weeklyTasks: workDayTasks,
  monthlyRules: z.array(monthlyTaskRuleRequest).max(SCHEDULE_MONTHLY_RULES_MAX),
  maxHours: workDayHours.nullable().optional(),
  durationLabel: optionalText(SCHEDULE_DURATION_LABEL_MAX_LENGTH),
  instructions: optionalText(SCHEDULE_INSTRUCTIONS_MAX_LENGTH),
  actingOrganization: optional(recordId.toLowerCase()),
};

export const scheduleRowCreateRequest = z
  .object({
    ...rowContent,
    weekdays: z
      .array(scheduleWeekday)
      .min(1)
      .max(7)
      .refine((weekdays) => new Set(weekdays).size === weekdays.length, "each weekday once"),
    staffing: z.array(newStaffingRequest).max(SCHEDULE_STAFFING_MAX),
  })
  .strict();
export type ScheduleRowCreateRequest = z.infer<typeof scheduleRowCreateRequest>;

export const scheduleRowDetailsRequest = z
  .object({
    ...rowContent,
    weekday: scheduleWeekday,
    staffing: z.array(staffingRequest).max(SCHEDULE_STAFFING_MAX),
  })
  .strict();
export type ScheduleRowDetailsRequest = z.infer<typeof scheduleRowDetailsRequest>;

export const scheduleRowArchiveRequest = contractReadQuery.strict();
export type ScheduleRowArchiveRequest = z.infer<typeof scheduleRowArchiveRequest>;

export const scheduleCopyRequest = z
  .object({
    from: recordId.toLowerCase(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type ScheduleCopyRequest = z.infer<typeof scheduleCopyRequest>;

export const scheduleChange = z.object({ rows: z.array(scheduleRow), sync: scheduleSync });
export type ScheduleChange = z.infer<typeof scheduleChange>;
